import _ from 'lodash';

import { StateType } from '../../../states';
import { BasketItemState, BasketItems, BasketMissionParamsState, BasketState } from '../../../states/basket';
import { BasketModes, BasketStatuses } from '../../../types/basket';
import { hasOtherOperationsToggled } from '../../../utils/operations';
import {
  selectCombinedBasketEligibleMissionData,
  selectSimpleBasketItemEligibleMissionData,
} from '../selectors/basket';

const getEligibleMissionAchievement = (
  globalState: StateType,
  basket: BasketState,
  oddsId?: number,
): BasketMissionParamsState | null => {
  let eligibleMissionParams = null;

  if (basket.mode === BasketModes.SIMPLE && oddsId && !hasOtherOperationsToggled(basket, basket.items[oddsId])) {
    eligibleMissionParams = selectSimpleBasketItemEligibleMissionData(globalState, { oddId: oddsId });
  } else if (basket.mode === BasketModes.COMBINED && !hasOtherOperationsToggled(basket)) {
    eligibleMissionParams = selectCombinedBasketEligibleMissionData(globalState);
  }

  if (eligibleMissionParams) {
    eligibleMissionParams = {
      missionId: +eligibleMissionParams.missionId,
      achievementId: eligibleMissionParams.achievementId,
    };
  }

  return eligibleMissionParams;
};

const recomputeNextMissionBasketState = (nextBasketState: BasketState, globalState: StateType) => {
  const { isCashout, status: basketStatus, mode: basketMode } = nextBasketState;

  if (isCashout || basketStatus === BasketStatuses.VALIDATED) {
    return nextBasketState;
  }

  const { basket: basketState = {} } = globalState ?? {};
  const tmpBasketState = nextBasketState ?? basketState;

  const nextBasketMission = getEligibleMissionAchievement(
    {
      ...globalState,
      basket: tmpBasketState,
    },
    nextBasketState,
  );
  const hasBasketMissionChanged = !_.isEqual(nextBasketMission, tmpBasketState.missionsParams);
  let hasBasketItemsChanged = false;

  const { items: basketItems } = tmpBasketState;
  const resetedBasketItems: BasketItems = {};

  for (const itemId of Object.keys(basketItems)) {
    resetedBasketItems[itemId] = basketItems[itemId];
    delete resetedBasketItems[itemId].missionsParams;
  }

  const nextItems = { ...resetedBasketItems };

  const sortingCallback = (a: BasketItemState, b: BasketItemState) => (a.sortBy ?? 0) - (b.sortBy ?? 0);
  const basketItemsAsArr = Object.keys(resetedBasketItems)
    .map((itemId) => resetedBasketItems[itemId])
    .sort(sortingCallback);

  for (const basketItem of basketItemsAsArr) {
    const { oddId: itemId } = basketItem;

    const eligibleMissionParams = getEligibleMissionAchievement(
      {
        ...globalState,
        basket: {
          ...tmpBasketState,
          items: nextItems,
        },
      },
      nextBasketState,
      itemId,
    );

    if (basketMode === BasketModes.SIMPLE) {
      if (eligibleMissionParams) {
        nextItems[itemId] = {
          ...nextItems[itemId],
          missionsParams: eligibleMissionParams,
        };

        hasBasketItemsChanged = true;
      } else {
        delete nextItems[itemId].missionsParams;
        hasBasketItemsChanged = true;
      }
    } else {
      delete nextItems[itemId].missionsParams;
      hasBasketItemsChanged = true;
    }
  }

  let nextState = tmpBasketState;

  if (hasBasketMissionChanged) {
    nextState = {
      ...tmpBasketState,
      missionsParams: nextBasketMission,
    };
  }

  if (hasBasketItemsChanged) {
    nextState = {
      ...nextState,
      items: nextItems,
    };
  }

  return nextState;
};

export default recomputeNextMissionBasketState;
